import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
  Modal,
  TextInput,
  Animated,
  Image,
} from 'react-native';
import TabBar from '../../components/TabBar';
import SimpleHeader from '../../components/SimpleHeader';
import PINDots from '../../components/UIComponents/PINDots';
import PinKeypad, { PIN_INPUT_PROPS } from '../../../web/PinKeypad';
import { COLORS } from '../../theme/themeTokens';
import { v2BlockCard, v2UnblockCard } from '../../services/v2/api';
import { usePIN } from '../../contexts/PINContext';
import { useCapabilities } from '../../hooks/useCapabilities';

// ──────────────────────────────────────────────────
// Props
// ──────────────────────────────────────────────────

export type CardBlockMode = 'block' | 'unblock';

interface CardBlockScreenProps {
  onBack: () => void;
  onNavigate: (screen: string) => void;
  cardId: string;
  /** Which action this screen confirms. Drives header title, colors, icon
   *  tint, endpoint call, and success alert. */
  mode: CardBlockMode;
}

// ──────────────────────────────────────────────────
// Mode-specific config
// ──────────────────────────────────────────────────
//
// Единая точка правды по различиям block vs unblock — при добавлении
// новых полей (например warning тексту), кладём сюда, а не через тернарники
// внутри JSX. Иконка одна и та же (lock_white), меняется только tint и
// background pill'а.

const MODE_CONFIG: Record<CardBlockMode, {
  title: string;
  heading: string;
  headingColor: string;
  description: string;
  iconPillBg: string;
  iconTint: string;
  confirmBg: string;
  successTitle: string;
  successMessage: string;
  modalSubtitle: string;
}> = {
  block: {
    title: 'Block the card',
    heading: 'Confirm blocking',
    headingColor: COLORS.brandOrange, // #C94021 (Primary-Brand-Orange)
    description: "Once confirmed, we'll send an urgent request to the manager. The card will be blocked within 15 minutes.",
    iconPillBg: '#FEE8E7',
    iconTint: COLORS.brandOrange,
    confirmBg: COLORS.brandOrange,
    successTitle: 'Card blocked',
    successMessage: 'Your card has been blocked successfully.',
    modalSubtitle: 'Enter your PIN to confirm blocking',
  },
  unblock: {
    title: 'Unblock the card',
    heading: 'Unblock the card',
    headingColor: COLORS.primaryNavy,
    description: 'Send request to manager to unblock your card.',
    iconPillBg: '#5EC07E',
    iconTint: '#0F1F3F',
    confirmBg: COLORS.primaryNavy,
    successTitle: 'Card unblocked',
    successMessage: 'Your card has been unblocked successfully.',
    modalSubtitle: 'Enter your PIN to confirm unblocking',
  },
};

// ──────────────────────────────────────────────────
// Component
// ──────────────────────────────────────────────────

const CardBlockScreen: React.FC<CardBlockScreenProps> = ({ onBack, onNavigate, cardId, mode }) => {
  const [loading, setLoading] = useState(false);
  const cfg = MODE_CONFIG[mode];

  const { verifyPIN, isPINSetup } = usePIN();
  const caps = useCapabilities();

  const [showPINModal, setShowPINModal] = useState(false);
  const [modalPIN, setModalPIN] = useState('');
  const [modalError, setModalError] = useState<string | null>(null);
  const modalInputRef = useRef<TextInput>(null);
  const shakeAnimation = useRef(new Animated.Value(0)).current;

  const shakeModal = () => {
    Animated.sequence([
      Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -10, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true }),
    ]).start();
  };

  const invokeCardAction = async () => {
    setLoading(true);
    try {
      if (mode === 'block') {
        await v2BlockCard(cardId);
      } else {
        await v2UnblockCard(cardId);
      }
      Alert.alert(cfg.successTitle, cfg.successMessage, [
        { text: 'OK', onPress: onBack },
      ]);
    } catch (error) {
      Alert.alert('Error', `Failed to ${mode} the card. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    // Without a PIN (skipped, or the web) there is nothing to ask — same as CardInfoScreen.
    if (!isPINSetup) {
      invokeCardAction();
      return;
    }
    setModalPIN('');
    setModalError(null);
    setShowPINModal(true);
  };

  const handleModalPINChange = (value: string) => {
    const digits = value.replace(/[^0-9]/g, '').slice(0, 4);
    setModalError(null);
    setModalPIN(digits);

    if (digits.length === 4) {
      setTimeout(async () => {
        const isValid = await verifyPIN(digits);
        if (isValid) {
          setShowPINModal(false);
          setModalPIN('');
          invokeCardAction();
        } else {
          shakeModal();
          setModalError('Wrong PIN. Try again.');
          setModalPIN('');
        }
      }, 200);
    }
  };

  const handleModalClose = () => {
    setShowPINModal(false);
    setModalPIN('');
    setModalError(null);
  };

  // caps.can_block_card is server-derived: false under impersonation
  // (both PUT and DELETE /cards/:id/block sit in impersonation hard-deny).
  const canAct = caps.can_block_card;

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      <SimpleHeader title={cfg.title} onBack={onBack} />

      <View style={styles.content}>
        <View style={[styles.iconPill, { backgroundColor: cfg.iconPillBg }]}>
          <Image
            source={require('../../assets/lock_white.png')}
            style={[styles.iconImg, { tintColor: cfg.iconTint }]}
            resizeMode="contain"
          />
        </View>

        <Text style={[styles.heading, { color: cfg.headingColor }]}>{cfg.heading}</Text>

        <Text style={styles.description}>{cfg.description}</Text>

        <TouchableOpacity
          style={[styles.confirmBtn, { backgroundColor: cfg.confirmBg }, !canAct && { opacity: 0.5 }]}
          activeOpacity={canAct ? 0.7 : 1}
          onPress={handleConfirm}
          disabled={loading || !canAct}
        >
          {loading ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.confirmBtnText}>
              {canAct ? 'Confirm' : 'Unavailable in login-as-user mode'}
            </Text>
          )}
        </TouchableOpacity>
      </View>

      <Modal
        visible={showPINModal}
        transparent
        animationType="fade"
        onRequestClose={handleModalClose}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={handleModalClose}
        >
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>Enter PIN</Text>
            <Text style={styles.modalSubtitle}>{cfg.modalSubtitle}</Text>

            <TextInput
              ref={modalInputRef}
              value={modalPIN}
              onChangeText={handleModalPINChange}
              keyboardType="number-pad"
              maxLength={4}
              secureTextEntry
              autoFocus
              style={styles.hiddenInput}
              caretHidden
              {...PIN_INPUT_PROPS}
            />

            <PINDots
              pin={modalPIN}
              pinLength={4}
              error={modalError}
              shakeAnimation={shakeAnimation}
              onPress={() => {
                modalInputRef.current?.blur();
                setTimeout(() => modalInputRef.current?.focus(), 50);
              }}
            />

            {modalError && <Text style={styles.modalError}>{modalError}</Text>}

            <PinKeypad value={modalPIN} length={4} onChange={handleModalPINChange} compact />

            <TouchableOpacity style={styles.modalCancelButton} onPress={handleModalClose}>
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

// ──────────────────────────────────────────────────
// Styles
// ──────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  content: {
    // Один сплошной pureLight фон под header'ом — по требованию убран
    // внутренний белый card-frame; содержимое лежит прямо на этом слое.
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    paddingTop: 24,
    alignItems: 'center',
  },
  iconPill: {
    width: 80,
    height: 80,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  iconImg: {
    width: 40,
    height: 40,
  },
  // Title2/Semibold — Inter 24/500/lineHeight 28.
  heading: {
    fontFamily: 'Inter',
    fontSize: 24,
    fontWeight: '500',
    lineHeight: 28,
    textAlign: 'center',
    marginBottom: 12,
  },
  // Body2/Regular — Inter 14/400, primaryNavy.
  description: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    color: COLORS.primaryNavy,
    textAlign: 'center',
    marginBottom: 32,
    paddingHorizontal: 20,
  },
  // 370×48 per design. maxWidth защищает на узких устройствах (<380 px);
  // alignSelf нужен потому что content = flex column with alignItems:center.
  confirmBtn: {
    width: 370,
    maxWidth: '100%',
    height: 48,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Body2/Medium — Inter 16/500/lineHeight 20.
  confirmBtnText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
    color: '#FFFFFF',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: COLORS.pureLight,
    borderRadius: 16,
    padding: 24,
    width: '85%',
    alignItems: 'center',
  },
  modalTitle: {
    fontFamily: 'Inter',
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.deepNavy,
    marginBottom: 8,
  },
  modalSubtitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    color: COLORS.blueSteel,
    marginBottom: 24,
    textAlign: 'center',
  },
  modalError: {
    fontFamily: 'Inter',
    fontSize: 15,
    color: '#E53935',
    marginTop: -8,
    marginBottom: 8,
  },
  modalCancelButton: {
    marginTop: 8,
    paddingVertical: 10,
    paddingHorizontal: 24,
  },
  modalCancelText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  hiddenInput: {
    position: 'absolute',
    opacity: 0,
    height: 1,
    width: 1,
  },
});

export default CardBlockScreen;
